import React, { useState } from "react";
import * as XLSX from "xlsx";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TruckIcon, ScaleIcon, BanIcon, FileTextIcon, Undo2Icon, ClipboardListIcon, FileSpreadsheetIcon, XCircleIcon } from "lucide-react";
import { toast } from "sonner";
import { EtapaBadge, EtapaActions, FINALIDADES, FIN_COR } from "@/components/EtapaOperacao";
import { fmtKg } from "./IndicadorBalanca";

const fmtDT = (s) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const elapsed = (iso, now) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((now - new Date(iso)) / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
};
const Placas = ({ r }) => (
  <div className="flex gap-1.5 flex-wrap items-center">
    {r.placa_cavalo && <span className="bg-white text-black font-mono font-black text-xs px-2 py-0.5 border border-black">{r.placa_cavalo}</span>}
    {r.placa_carreta && <span className="bg-white/90 text-black font-mono font-bold text-xs px-2 py-0.5 border border-black">{r.placa_carreta}</span>}
    {r.frota && <span className="bg-[#00E5FF] text-black font-mono font-bold text-[10px] px-1.5 py-0.5 border border-black">FROTA {r.frota}</span>}
    {!r.placa_cavalo && !r.placa_carreta && <span className="text-muted-foreground text-xs">—</span>}
  </div>
);
const TipoOp = ({ tipo }) => (
  <span className={`text-[9px] font-bold px-1.5 border ${tipo === "CARGA" ? "text-[#34C759] border-[#34C759]/50" : tipo === "DESCARGA" ? "text-[#00E5FF] border-[#00E5FF]/50" : "text-[#FFD60A] border-[#FFD60A]/50"}`}>{tipo}</span>
);

const STATUS_PES = {
  PENDENTE: { txt: "Aguardando decisão", cls: "text-[#FF9F0A] border-[#FF9F0A]/50 bg-[#FF9F0A]/10" },
  ABERTO: { txt: "1ª pesagem feita · aguarda 2ª", cls: "text-[#FFD60A] border-[#FFD60A]/50 bg-[#FFD60A]/10" },
  FINALIZADO: { txt: "Pesagem concluída", cls: "text-[#34C759] border-[#34C759]/50 bg-[#34C759]/10" },
  SEM_BALANCA: { txt: "Não passa na balança", cls: "text-muted-foreground border-border bg-white/5" },
};

/** Carretas liberadas pela Portaria (em operação) com decisão/ações de pesagem + etapas. */
export function LiberadasTable({ itens, now, onPesar1, onPesar2, onTicket, reload }) {
  const semBalanca = async (op, desfazer) => {
    try {
      await api.post(`/balanca/operacoes/${op.id}/sem-balanca`, { desfazer });
      toast.success(desfazer ? "Marcação desfeita — carreta volta a aguardar pesagem" : `${op.placa_cavalo || op.nome}: não passa na balança`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto">
      <div className="p-4 border-b border-border text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] flex items-center gap-2">
        <TruckIcon className="w-4 h-4" /> Liberadas pela Portaria — na base agora ({itens.length})
      </div>
      <table className="w-full text-sm" data-testid="balanca-tabela-liberadas">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
            <th className="px-4 py-2">Placas / Frota</th>
            <th className="px-4 py-2">Motorista</th>
            <th className="px-4 py-2">Operação</th>
            <th className="px-4 py-2">Entrada</th>
            <th className="px-4 py-2">Etapa</th>
            <th className="px-4 py-2">Pesagem</th>
            <th className="px-4 py-2 text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {itens.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground text-xs">Nenhuma carreta liberada pela Portaria no momento.</td></tr>}
          {itens.map((o) => {
            const st = STATUS_PES[o.pesagem_status] || STATUS_PES.PENDENTE;
            return (
              <tr key={o.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`liberada-${o.id}`}>
                <td className="px-4 py-3"><Placas r={o} /></td>
                <td className="px-4 py-3"><div className="font-semibold text-white">{o.nome}</div><div className="text-[10px] text-muted-foreground">{o.empresa || "—"}</div></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap items-center">
                    <TipoOp tipo={o.tipo_operacao} />
                    {(o.finalidades || []).map((f) => (
                      <Badge key={f} className={`rounded-none text-[9px] border ${o.etapas?.[f] === "CANCELADO" ? "line-through opacity-50" : ""}`}
                             style={{ color: FIN_COR[f], borderColor: `${FIN_COR[f]}66`, background: `${FIN_COR[f]}1A` }}>{FINALIDADES[f]}</Badge>
                    ))}
                    {o.destino && <span className="text-[10px] font-mono font-bold text-[#34C759]">→ {o.destino}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[#34C759]">{fmtDT(o.entrou_em)} <span className="text-muted-foreground">· {elapsed(o.entrou_em, now)}</span></td>
                <td className="px-4 py-3"><EtapaBadge op={o} testid={`balanca-etapa-${o.id}`} /></td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border ${st.cls}`} data-testid={`pesagem-status-${o.id}`}>{st.txt}</span>
                  {o.pesagem && <div className="text-[10px] font-mono text-muted-foreground mt-1">{o.pesagem.ticket_str} · 1ª {fmtKg(o.pesagem.peso1)}{o.pesagem.peso2 != null && <> · 2ª {fmtKg(o.pesagem.peso2)} · líq <b className="text-[#34C759]">{fmtKg(o.pesagem.peso_liquido)}</b></>}</div>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1.5 flex-wrap">
                    {o.pesagem_status === "PENDENTE" && (
                      <>
                        <Button size="sm" onClick={() => onPesar1(o)} data-testid={`btn-pesar1-${o.id}`} className="rounded-none h-8 bg-[#FFD60A] text-black hover:bg-[#FFD60A]/85 font-bold uppercase text-[10px]">
                          <ScaleIcon className="w-3.5 h-3.5 mr-1" /> 1ª Pesagem
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => semBalanca(o, false)} data-testid={`btn-sem-balanca-${o.id}`} className="rounded-none h-8 border-border text-muted-foreground hover:text-white font-bold uppercase text-[10px]">
                          <BanIcon className="w-3.5 h-3.5 mr-1" /> Não passa na balança
                        </Button>
                      </>
                    )}
                    {o.pesagem_status === "ABERTO" && (
                      <Button size="sm" onClick={() => onPesar2(o.pesagem)} data-testid={`btn-pesar2-${o.id}`} className="rounded-none h-8 bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase text-[10px]">
                        <ScaleIcon className="w-3.5 h-3.5 mr-1" /> 2ª Pesagem
                      </Button>
                    )}
                    {o.pesagem && (
                      <Button size="sm" variant="outline" onClick={() => onTicket(o.pesagem)} data-testid={`btn-ticket-${o.id}`} className="rounded-none h-8 border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10 font-bold uppercase text-[10px]">
                        <FileTextIcon className="w-3.5 h-3.5 mr-1" /> Ticket
                      </Button>
                    )}
                    {o.pesagem_status === "SEM_BALANCA" && (
                      <Button size="sm" variant="ghost" onClick={() => semBalanca(o, true)} data-testid={`btn-desfazer-sem-balanca-${o.id}`} className="rounded-none h-8 text-muted-foreground hover:text-white font-bold uppercase text-[10px]">
                        <Undo2Icon className="w-3.5 h-3.5 mr-1" /> Desfazer
                      </Button>
                    )}
                    <EtapaActions op={o} onDone={reload} compact />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

const STATUS_TK = {
  ABERTO: "text-[#FFD60A] border-[#FFD60A]/50 bg-[#FFD60A]/10",
  FINALIZADO: "text-[#34C759] border-[#34C759]/50 bg-[#34C759]/10",
  CANCELADO: "text-[#FF3B30] border-[#FF3B30]/50 bg-[#FF3B30]/10 line-through",
};

const exportarExcel = (tickets) => {
  const linhas = tickets.map((t) => ({
    "Ticket": t.ticket_str, "Status": t.status, "Operação": t.tipo_operacao, "Origem": t.avulsa ? "AVULSA" : "PORTARIA",
    "Placa Cavalo": t.placa_cavalo || "", "Placa Carreta": t.placa_carreta || "", "Frota": t.frota || "",
    "Motorista": t.nome || "", "Empresa": t.empresa || "", "Finalidades": (t.finalidades || []).map((f) => FINALIDADES[f]).join(" + "),
    "Destino": t.destino || "", "Nota Fiscal": t.nota_fiscal || "", "Produto": t.produto || "",
    "1ª Pesagem (kg)": t.peso1 ?? "", "1ª Pesagem em": t.peso1_em ? new Date(t.peso1_em).toLocaleString("pt-BR") : "", "1ª Origem": t.peso1_origem || "", "1ª Por": t.peso1_por || "",
    "2ª Pesagem (kg)": t.peso2 ?? "", "2ª Pesagem em": t.peso2_em ? new Date(t.peso2_em).toLocaleString("pt-BR") : "", "2ª Origem": t.peso2_origem || "", "2ª Por": t.peso2_por || "",
    "Peso Líquido (kg)": t.peso_liquido ?? "", "Observações": t.observacoes || "", "Cancelamento": t.cancelado_motivo || "",
  }));
  const ws = XLSX.utils.json_to_sheet(linhas);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pesagens");
  XLSX.writeFile(wb, `tickets_pesagem_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

/** Histórico de tickets com busca, período e exportação. */
export function TicketsTable({ tickets, filtros, setFiltros, onBuscar, onTicket, onPesar2, reload, podeCancelar }) {
  const [cancel, setCancel] = useState(null);
  const [motivo, setMotivo] = useState("");
  const cancelar = async () => {
    try {
      await api.post(`/balanca/pesagens/${cancel.id}/cancelar`, { motivo });
      toast.success(`Ticket ${cancel.ticket_str} cancelado`); setCancel(null); setMotivo(""); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto" data-testid="balanca-tickets">
      <div className="p-4 border-b border-border flex items-end justify-between flex-wrap gap-3">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] flex items-center gap-2"><ClipboardListIcon className="w-4 h-4" /> Tickets de pesagem ({tickets.length})</div>
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label className="text-[9px] uppercase text-muted-foreground">Buscar</Label>
            <Input value={filtros.q} onChange={(e) => setFiltros({ ...filtros, q: e.target.value })} placeholder="ticket, placa, frota, NF, motorista" className="bg-[#0A0A0C] border-border rounded-none h-9 text-xs w-56" data-testid="tickets-busca" /></div>
          <div><Label className="text-[9px] uppercase text-muted-foreground">De</Label>
            <Input type="date" value={filtros.de} onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} className="bg-[#0A0A0C] border-border rounded-none h-9 text-xs" data-testid="tickets-de" /></div>
          <div><Label className="text-[9px] uppercase text-muted-foreground">Até</Label>
            <Input type="date" value={filtros.ate} onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} className="bg-[#0A0A0C] border-border rounded-none h-9 text-xs" data-testid="tickets-ate" /></div>
          <select value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })} className="bg-[#0A0A0C] border border-border rounded-none h-9 text-xs px-2" data-testid="tickets-status">
            <option value="">Todos</option><option value="ABERTO">Abertos</option><option value="FINALIZADO">Finalizados</option><option value="CANCELADO">Cancelados</option>
          </select>
          <Button size="sm" onClick={onBuscar} data-testid="tickets-buscar" className="rounded-none h-9 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 font-bold uppercase text-[10px]">Buscar</Button>
          {tickets.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => exportarExcel(tickets)} data-testid="tickets-excel" className="rounded-none h-9 border-[#34C759] text-[#34C759] hover:bg-[#34C759]/10 font-bold uppercase text-[10px]">
              <FileSpreadsheetIcon className="w-4 h-4 mr-1" /> Excel
            </Button>
          )}
        </div>
      </div>
      <table className="w-full text-sm" data-testid="tickets-tabela">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
            <th className="px-4 py-2">Ticket</th><th className="px-4 py-2">Veículo / Motorista</th><th className="px-4 py-2">Operação</th>
            <th className="px-4 py-2">1ª Pesagem</th><th className="px-4 py-2">2ª Pesagem</th><th className="px-4 py-2">Líquido</th><th className="px-4 py-2">Status</th><th className="px-4 py-2 text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {tickets.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground text-xs">Nenhum ticket no período.</td></tr>}
          {tickets.map((t) => (
            <tr key={t.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`ticket-${t.id}`}>
              <td className="px-4 py-2"><div className="font-mono font-bold text-white">{t.ticket_str}</div><div className="text-[10px] text-muted-foreground">{fmtDT(t.criado_em)}</div>{t.avulsa && <span className="text-[9px] font-bold px-1 border border-[#FF9F0A]/60 text-[#FF9F0A]" data-testid={`ticket-avulsa-${t.id}`}>AVULSA</span>}</td>
              <td className="px-4 py-2"><Placas r={t} /><div className="text-[11px] text-muted-foreground mt-0.5">{t.nome}{t.empresa ? ` · ${t.empresa}` : ""}</div></td>
              <td className="px-4 py-2"><TipoOp tipo={t.tipo_operacao} />{(t.nota_fiscal || t.produto) && <div className="text-[10px] text-muted-foreground mt-0.5">{t.nota_fiscal && `NF ${t.nota_fiscal}`}{t.nota_fiscal && t.produto && " · "}{t.produto}</div>}</td>
              <td className="px-4 py-2 font-mono text-xs"><span className="text-[#FFD60A] font-bold">{fmtKg(t.peso1)}</span><div className="text-[10px] text-muted-foreground">{fmtDT(t.peso1_em)} · {t.peso1_origem}</div></td>
              <td className="px-4 py-2 font-mono text-xs">{t.peso2 != null ? <><span className="text-[#FFD60A] font-bold">{fmtKg(t.peso2)}</span><div className="text-[10px] text-muted-foreground">{fmtDT(t.peso2_em)} · {t.peso2_origem}</div></> : <span className="text-muted-foreground">pendente</span>}</td>
              <td className="px-4 py-2 font-mono font-black text-[#34C759]">{fmtKg(t.peso_liquido)}</td>
              <td className="px-4 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-1 border ${STATUS_TK[t.status]}`}>{t.status}</span></td>
              <td className="px-4 py-2 text-right">
                <div className="flex justify-end gap-1.5">
                  {t.status === "ABERTO" && <Button size="sm" onClick={() => onPesar2(t)} data-testid={`ticket-pesar2-${t.id}`} className="rounded-none h-8 bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase text-[10px]">2ª Pesagem</Button>}
                  <Button size="sm" variant="outline" onClick={() => onTicket(t)} data-testid={`ticket-ver-${t.id}`} className="rounded-none h-8 border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10 font-bold uppercase text-[10px]"><FileTextIcon className="w-3.5 h-3.5" /></Button>
                  {podeCancelar && t.status !== "CANCELADO" && <Button size="sm" variant="ghost" onClick={() => setCancel(t)} data-testid={`ticket-cancelar-${t.id}`} className="rounded-none h-8 text-[#FF3B30] hover:bg-[#FF3B30]/10"><XCircleIcon className="w-3.5 h-3.5" /></Button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {cancel && (
        <div className="p-4 border-t border-[#FF3B30]/40 bg-[#FF3B30]/5 flex items-end gap-2 flex-wrap" data-testid="ticket-cancelar-bloco">
          <div className="flex-1 min-w-[240px]"><Label className="text-[10px] uppercase text-[#FF3B30]">Cancelar ticket {cancel.ticket_str} — motivo</Label>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} className="bg-[#0A0A0C] border-border rounded-none h-9 text-xs" data-testid="ticket-cancelar-motivo" /></div>
          <Button size="sm" onClick={cancelar} data-testid="ticket-cancelar-confirmar" className="rounded-none h-9 bg-[#FF3B30] text-white hover:bg-[#FF3B30]/85 font-bold uppercase text-[10px]">Confirmar cancelamento</Button>
          <Button size="sm" variant="ghost" onClick={() => setCancel(null)} className="rounded-none h-9 text-[10px] uppercase">Voltar</Button>
        </div>
      )}
    </Card>
  );
}
